/**
 * Portal session — the signed-in user's role + identity, read from the JWT via
 * `GET /api/iam/auth/me` (the only portal-reachable endpoint that returns the
 * `role` claim; the account endpoint does not). The shell branches its
 * navigation and route-away guard on this role.
 *
 * httpOnly cookies are not readable from JS, so the role must come from the API.
 */
import { useQuery } from '@tanstack/react-query';
import { http } from '@teivor/shared-utils/api';

export type PortalRole = 'client' | 'client_salesman' | (string & {});

export interface PortalSession {
  id: string;
  email: string;
  role: PortalRole;
  customRoleId: string | null;
  impersonating: boolean;
}

export const sessionKey = ['session'] as const;

export function useSession() {
  return useQuery<PortalSession>({
    queryKey: sessionKey,
    queryFn: () => http.get<PortalSession>('/api/iam/auth/me'),
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    retry: false,
  });
}

/** A workspace sales rep — sees the rep surface (My leads), never the client surface. */
export function isRep(role: string | undefined): boolean {
  return role === 'client_salesman';
}
